import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Plus, Eye, Edit2, Trash2, ChevronLeft, ChevronRight, FileDown, X } from 'lucide-react'
import { pedidoService } from '../../services/pedidoService'
import { industriaService } from '../../services/industriaService'
import { browserNotificationService } from '../../services/browserNotificationService'
import { useWebSocketStore } from '../../store/websocketStore'
import type { Pedido, Industria } from '../../types'
import { formatDate, getIndustriaColorClasses, getIndustriaInlineStyles } from '../../utils/helpers'
import { TableSkeleton } from '../../components/ui/Skeleton'
import { Modal } from '../../components/ui/Modal'
import { toast } from '../../utils/toast'

export function PedidosPage() {
  const navigate = useNavigate()
  const [subpedidos, setSubpedidos] = useState<Pedido[]>([])
  const [industrias, setIndustrias] = useState<Industria[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Tabs por indústria
  const [tabIndustriaId, setTabIndustriaId] = useState('')

  // Filtros
  const [searchCliente, setSearchCliente] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'pendente' | 'processando' | 'concluido' | 'cancelado'>('all')
  const [filterDataInicio, setFilterDataInicio] = useState('')
  const [filterDataFim, setFilterDataFim] = useState('')

  // Paginação controlada pela API
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [exporting, setExporting] = useState(false)

  // Busca por cliente com debounce
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const loadPedidos = useCallback(async () => {
    setLoading(true)
    try {
      const result = await pedidoService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        status: filterStatus === 'all' ? undefined : filterStatus,
        industriaId: tabIndustriaId || undefined,
        subpedidos: true,
        dataInicio: filterDataInicio || undefined,
        dataFim: filterDataFim || undefined,
        search: debouncedSearch || undefined,
      })

      const mapped = (result.data as Pedido[]).map((subpedido) => {
        const industriaInfo = subpedido.industrias?.[0] || subpedido.industria
        return {
          ...subpedido,
          industria: subpedido.industria || (industriaInfo ? {
            id: industriaInfo.industriaId || industriaInfo.id,
            nomeFantasia: industriaInfo.nomeFantasia,
            cor: industriaInfo.cor,
          } : undefined),
          pedidoId: subpedido.pedidoId || subpedido.id,
        }
      })
      setSubpedidos(mapped)
      setTotalPages(result.pagination.totalPages)
      setTotalItems(result.pagination.total)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar pedidos'
      toast.error(message)
      console.error('Erro ao carregar pedidos:', error)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }, [currentPage, itemsPerPage, filterStatus, tabIndustriaId, filterDataInicio, filterDataFim, debouncedSearch])

  const loadIndustrias = useCallback(async () => {
    try {
      const result = await industriaService.getAll({ status: 'ativo', limit: 1000 })
      setIndustrias(result.data)
    } catch (error) {
      console.error('Erro ao carregar indústrias:', error)
    }
  }, [])

  useEffect(() => {
    loadIndustrias()
  }, [loadIndustrias])

  // Carrega pedidos quando filtros, paginação ou busca debounced mudam
  useEffect(() => {
    loadPedidos()
  }, [loadPedidos])

  // Debounce para busca de cliente e reset de página
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      setDebouncedSearch(searchCliente)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchCliente])

  // Reseta página quando filtros fixos mudam
  useEffect(() => {
    setCurrentPage(1)
  }, [itemsPerPage, filterStatus, tabIndustriaId, filterDataInicio, filterDataFim])

  // WebSocket: Atualização em tempo real
  useEffect(() => {
    const componentId = 'pedidos-page'

    // Registra callbacks no store
    useWebSocketStore.getState().onPedidoCriado(componentId, (data) => {
      console.log('🔄 [PedidosPage] Novo pedido criado via WebSocket:', data.pedidoId)
      loadPedidos()
    })

    useWebSocketStore.getState().onPedidoStatusAlterado(componentId, (data) => {
      console.log('🔄 [PedidosPage] Status alterado via WebSocket:', data.pedidoId, data.statusNovo)

      // Atualiza os subpedidos na lista local (sem recarregar)
      setSubpedidos(prevSubpedidos =>
        prevSubpedidos.map(subpedido =>
          subpedido.pedidoId === data.pedidoId
            ? { ...subpedido, status: data.statusNovo as Pedido['status'] }
            : subpedido
        )
      )

      // Se o modal estiver aberto com este pedido, atualiza também
      if (selectedPedido?.id === data.pedidoId) {
        setSelectedPedido(prev =>
          prev ? { ...prev, status: data.statusNovo as Pedido['status'] } : null
        )
      }
    })

    useWebSocketStore.getState().onPedidoCancelado(componentId, (data) => {
      console.log('🔄 [PedidosPage] Pedido cancelado via WebSocket:', data.pedidoId)

      // Atualiza o status para cancelado nos subpedidos
      setSubpedidos(prevSubpedidos =>
        prevSubpedidos.map(subpedido =>
          subpedido.pedidoId === data.pedidoId
            ? { ...subpedido, status: 'cancelado' as Pedido['status'] }
            : subpedido
        )
      )

      // Se o modal estiver aberto com este pedido, atualiza também
      if (selectedPedido?.id === data.pedidoId) {
        setSelectedPedido(prev =>
          prev ? { ...prev, status: 'cancelado' as Pedido['status'] } : null
        )
      }
    })

    // Cleanup: Remove callbacks quando o componente desmonta
    return () => {
      useWebSocketStore.getState().offPedidoCriado(componentId)
      useWebSocketStore.getState().offPedidoStatusAlterado(componentId)
      useWebSocketStore.getState().offPedidoCancelado(componentId)
    }
  }, [loadPedidos, selectedPedido])

  const viewPedido = async (pedido: Pedido) => {
    setIsModalOpen(true)
    setLoadingDetails(true)

    try {
      // Buscar detalhes completos do pedido pai
      const pedidoCompleto = await pedidoService.getById(pedido.pedidoId || pedido.id)
      setSelectedPedido(pedidoCompleto)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar detalhes do pedido'
      toast.error(message)
      console.error('Erro ao carregar detalhes:', error)
      setIsModalOpen(false)
    } finally {
      setLoadingDetails(false)
    }
  }

  const deletePedido = async (pedido: Pedido) => {
    const pedidoId = pedido.pedidoId || pedido.id
    const temGrupo = !!pedido.grupoId

    let excluirGrupo = false
    if (temGrupo) {
      const choice = confirm(
        `Este pedido faz parte de um grupo.\n\n` +
        `Clique em OK para excluir TODOS os pedidos do mesmo grupo.\n` +
        `Clique em Cancelar para excluir apenas este pedido.`
      )
      excluirGrupo = choice
    } else {
      if (!confirm('Tem certeza que deseja excluir este pedido?')) {
        return
      }
    }

    try {
      await pedidoService.delete(pedidoId, excluirGrupo)
      await loadPedidos()
      toast.success(excluirGrupo ? 'Pedidos do grupo excluídos com sucesso' : 'Pedido excluído com sucesso')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir pedido'
      toast.error(message)
      console.error('Erro ao excluir pedido:', error)
    }
  }

  const updateStatus = async (pedido: Pedido, newStatus: 'pendente' | 'processando' | 'concluido' | 'cancelado') => {
    try {
      const pedidoId = pedido.pedidoId || pedido.id
      await pedidoService.updateStatus(pedidoId, newStatus)
      await loadPedidos()
      
      // Disparar notificação do browser
      const orderNumber = pedido.numero || `#${pedidoId.slice(0, 8).toUpperCase()}`
      browserNotificationService.notifyStatusChange(orderNumber, newStatus)
      
      toast.success(`Status alterado para ${getStatusLabel(newStatus)}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao atualizar status'
      toast.error(message)
      console.error('Erro ao atualizar status:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
      case 'processando':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
      case 'concluido':
        return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
      case 'cancelado':
        return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'Pendente'
      case 'processando':
        return 'Em Processamento'
      case 'concluido':
        return 'Concluído'
      case 'cancelado':
        return 'Cancelado'
      default:
        return status
    }
  }

  const getSubpedidoCor = (subpedido: Pedido) => {
    return subpedido.industria?.cor || subpedido.cor
  }

  const getSubpedidoStyle = (subpedido: Pedido) => {
    const cor = getSubpedidoCor(subpedido)
    const classes = getIndustriaColorClasses(cor)
    const inline = getIndustriaInlineStyles(cor)
    return { classes, inline }
  }

  const limparFiltros = () => {
    setSearchCliente('')
    setDebouncedSearch('')
    setFilterStatus('all')
    setFilterDataInicio('')
    setFilterDataFim('')
    setTabIndustriaId('')
    setCurrentPage(1)
  }

  const exportarRelatorio = async () => {
    setExporting(true)
    try {
      const blob = await pedidoService.exportarRelatorio({
        status: filterStatus === 'all' ? undefined : filterStatus,
        industriaId: tabIndustriaId || undefined,
        dataInicio: filterDataInicio || undefined,
        dataFim: filterDataFim || undefined,
        search: searchCliente || undefined,
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `relatorio_pedidos_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Relatório exportado com sucesso!')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao exportar relatório'
      toast.error(message)
      console.error('Erro ao exportar relatório:', error)
    } finally {
      setExporting(false)
    }
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pedidos</h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={exportarRelatorio}
              isLoading={exporting}
              disabled={exporting || subpedidos.length === 0}
              className="w-full sm:w-auto"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Exportar
            </Button>
            <Button onClick={() => navigate('/pedidos/novo')} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Novo Pedido
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <Card title="Filtros">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Cliente
              </label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={searchCliente}
                  onChange={(e) => setSearchCliente(e.target.value)}
                  className="pr-9"
                />
                {searchCliente && (
                  <button
                    type="button"
                    onClick={() => setSearchCliente('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label="Limpar busca"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="processando">Em Processamento</option>
                <option value="concluido">Concluído</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Data Início
              </label>
              <Input
                type="date"
                value={filterDataInicio}
                onChange={(e) => setFilterDataInicio(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Data Fim
              </label>
              <Input
                type="date"
                value={filterDataFim}
                onChange={(e) => setFilterDataFim(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mostrando {subpedidos.length} de {totalItems} pedidos
              </p>
              {(searchCliente || filterStatus !== 'all' || filterDataInicio || filterDataFim || tabIndustriaId) && (
                <button
                  type="button"
                  onClick={limparFiltros}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Limpar filtros
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Itens por página:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Tabs por Indústria */}
        <div className="flex flex-wrap gap-2 animate-fadeIn">
          <button
            onClick={() => {
              setTabIndustriaId('')
              setCurrentPage(1)
            }}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              tabIndustriaId === ''
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            Todas
          </button>
          {industrias.map(industria => (
            <button
              key={industria.id}
              onClick={() => {
                setTabIndustriaId(industria.id)
                setCurrentPage(1)
              }}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tabIndustriaId === industria.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {industria.cor && (
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: industria.cor }}
                />
              )}
              {industria.nomeFantasia}
            </button>
          ))}
        </div>

        <Card>
          {loading && isInitialLoad ? (
            <TableSkeleton />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto animate-fadeIn" style={{ minHeight: '200px' }}>
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Indústria
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Nº Pedido
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Data
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Cliente
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Itens
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Status
                      </th>
                      <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {subpedidos.map((subpedido) => {
                      const { classes, inline } = getSubpedidoStyle(subpedido)
                      return (
                      <tr
                        key={subpedido.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 ${classes.border}`}
                        style={inline ? { borderLeftColor: inline.borderColor } : undefined}
                      >
                        <td className="py-4 pl-3">
                          <span
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border shadow-sm ${classes.border} ${classes.bgLight} ${classes.text}`}
                            style={inline ? {
                              borderColor: inline.borderColor,
                              backgroundColor: inline.backgroundColorLight,
                              color: inline.color,
                            } : undefined}
                            title={subpedido.industria?.nomeFantasia || subpedido.nomeFantasia || '-'}
                          >
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                            />
                            {subpedido.industria?.nomeFantasia || subpedido.nomeFantasia || '-'}
                          </span>
                        </td>
                        <td className="py-4 text-sm font-mono font-semibold text-gray-900 dark:text-white">
                          {subpedido.numero || (subpedido.pedidoId ? `#${subpedido.pedidoId.slice(0, 8).toUpperCase()}` : `#${subpedido.id.slice(0, 8).toUpperCase()}`)}
                        </td>
                        <td className="py-4 text-sm text-gray-900 dark:text-white">
                          {formatDate(subpedido.createdAt)}
                        </td>
                        <td className="py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {subpedido.cliente?.razaoSocial || 'Cliente não encontrado'}
                        </td>
                        <td className="py-4 text-sm text-gray-700 dark:text-gray-300">
                          {subpedido.totalItens || subpedido.itens?.reduce((sum, item) => sum + item.quantidade, 0) || 0}
                        </td>
                        <td className="py-4">
                          <div className="flex flex-col gap-2">
                            <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(subpedido.status)}`}>
                              {getStatusLabel(subpedido.status)}
                            </span>
                            {subpedido.status === 'processando' && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => updateStatus(subpedido, 'concluido')}
                                  className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60"
                                >
                                  Concluir
                                </button>
                              </div>
                            )}
                            {subpedido.status === 'concluido' && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => updateStatus(subpedido, 'processando')}
                                  className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/60"
                                >
                                  Reabrir
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/pedidos/editar/${subpedido.pedidoId || subpedido.id}`)}
                              title="Editar pedido"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewPedido(subpedido)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePedido(subpedido)}
                              title="Excluir pedido"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>

                {subpedidos.length === 0 && (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhum pedido encontrado
                    </p>
                    <Button
                      className="mt-4"
                      onClick={() => navigate('/pedidos/novo')}
                    >
                      Criar Primeiro Pedido
                    </Button>
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 animate-fadeIn">
                {subpedidos.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhum pedido encontrado
                    </p>
                    <Button
                      className="mt-4"
                      onClick={() => navigate('/pedidos/novo')}
                    >
                      Criar Primeiro Pedido
                    </Button>
                  </div>
                ) : (
                  subpedidos.map((subpedido) => {
                    const { classes, inline } = getSubpedidoStyle(subpedido)
                    return (
                    <div
                      key={subpedido.id}
                      className={`rounded-lg border-l-4 border-y border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm ${classes.border}`}
                      style={inline ? { borderLeftColor: inline.borderColor } : undefined}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border shadow-sm ${classes.border} ${classes.bgLight} ${classes.text}`}
                          style={inline ? {
                            borderColor: inline.borderColor,
                            backgroundColor: inline.backgroundColorLight,
                            color: inline.color,
                          } : undefined}
                          title={subpedido.industria?.nomeFantasia || subpedido.nomeFantasia || '-'}
                        >
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                          />
                          {subpedido.industria?.nomeFantasia || subpedido.nomeFantasia || '-'}
                        </span>
                        <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(subpedido.status)}`}>
                          {getStatusLabel(subpedido.status)}
                        </span>
                      </div>

                      <div className="mb-3">
                        <h3 className="font-mono font-semibold text-gray-900 dark:text-white">
                          {subpedido.numero || (subpedido.pedidoId ? `#${subpedido.pedidoId.slice(0, 8).toUpperCase()}` : `#${subpedido.id.slice(0, 8).toUpperCase()}`)}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(subpedido.createdAt)}
                        </p>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Cliente:</span>
                          <p className="font-medium text-gray-900 dark:text-white mt-1">
                            {subpedido.cliente?.razaoSocial || 'Cliente não encontrado'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Itens:</span>
                          <p className="font-medium text-gray-900 dark:text-white mt-1">
                            {subpedido.totalItens || subpedido.itens?.reduce((sum, item) => sum + item.quantidade, 0) || 0}
                          </p>
                        </div>
                      </div>

                      {/* Ações rápidas de status */}
                      {subpedido.status === 'processando' && (
                        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => updateStatus(subpedido, 'concluido')}
                            className="w-full text-sm px-3 py-2 rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60"
                          >
                            ✓ Concluir Pedido
                          </button>
                        </div>
                      )}
                      {subpedido.status === 'concluido' && (
                        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => updateStatus(subpedido, 'processando')}
                            className="w-full text-sm px-3 py-2 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/60"
                          >
                            ← Reabrir Pedido
                          </button>
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/pedidos/editar/${subpedido.pedidoId || subpedido.id}`)}
                          title="Editar pedido"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewPedido(subpedido)}
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deletePedido(subpedido)}
                          title="Excluir pedido"
                          className="text-red-600 hover:text-red-700 dark:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )})
                )}
              </div>
            </>
          )}

          {/* Paginação */}
          {subpedidos.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Modal de visualização */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedPedido(null)
        }}
        title="Detalhes do Pedido"
        size="lg"
      >
        {loadingDetails ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : selectedPedido ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Número do Pedido</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  #{selectedPedido.id.substring(0, 8).toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Data</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(selectedPedido.createdAt)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Cliente</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedPedido.cliente?.razaoSocial || 'Não informado'}
                </p>
                {selectedPedido.cliente?.cnpj && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    CNPJ: {selectedPedido.cliente.cnpj}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Criado por</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedPedido.user?.name || selectedPedido.usuario?.name || 'Não informado'}
                </p>
                {(selectedPedido.user?.email || selectedPedido.usuario?.email) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedPedido.user?.email || selectedPedido.usuario?.email}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(selectedPedido.status)}`}>
                    {getStatusLabel(selectedPedido.status)}
                  </span>
                  {selectedPedido.status === 'pendente' && (
                    <button
                      onClick={() => {
                        updateStatus(selectedPedido, 'processando')
                        setIsModalOpen(false)
                      }}
                      className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/60"
                    >
                      Processar
                    </button>
                  )}
                  {selectedPedido.status === 'processando' && (
                    <button
                      onClick={() => {
                        updateStatus(selectedPedido, 'concluido')
                        setIsModalOpen(false)
                      }}
                      className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60"
                    >
                      Concluir
                    </button>
                  )}
                  {(selectedPedido.status === 'concluido' || selectedPedido.status === 'cancelado') && (
                    <button
                      onClick={() => {
                        updateStatus(selectedPedido, 'processando')
                        setIsModalOpen(false)
                      }}
                      className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/60"
                    >
                      Reabrir
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                Itens do Pedido
              </h3>
              {selectedPedido.industrias && selectedPedido.industrias.length > 0 ? (
                selectedPedido.industrias.map((pi) => (
                  <div
                    key={pi.id || pi.industriaId}
                    className="mb-4 rounded-lg border-l-4 border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
                    style={{ borderLeftColor: pi.cor || pi.industria?.cor || '#3B82F6' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {pi.nomeFantasia || pi.industria?.nomeFantasia || 'Indústria não identificada'}
                      </h4>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 block">
                          {pi.totalItens || (pi.itens?.reduce((sum, item) => sum + item.quantidade, 0)) || 0} {((pi.totalItens || 0) === 1) ? 'item' : 'itens'}
                        </span>
                        {(() => {
                          const subtotal = pi.itens?.reduce((sum, item) => 
                            sum + (item.quantidade * (item.preco || 0)), 0
                          ) || 0
                          return subtotal > 0 ? (
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              R$ {subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : null
                        })()}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {pi.itens && pi.itens.length > 0 ? (
                        pi.itens.map((item) => (
                          <div
                            key={item.produtoId}
                            className="space-y-1"
                          >
                            <div className="flex justify-between text-sm gap-2">
                              <span className="text-base text-gray-700 dark:text-gray-300 whitespace-normal break-words flex-1">
                                {item.produto?.codigo || 'N/A'} - {item.produto?.nome || 'Produto não identificado'}
                              </span>
                              <span className="font-medium text-blue-600 dark:text-blue-400">
                                {item.quantidade} {item.quantidade === 1 ? 'unidade' : 'unidades'}
                              </span>
                            </div>
                            {item.preco !== undefined && item.preco > 0 && (
                              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 pl-2">
                                <span>R$ {item.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × {item.quantidade}</span>
                                <span className="font-semibold">
                                  = R$ {(item.quantidade * item.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {pi.totalItens ? `${pi.totalItens} ${pi.totalItens === 1 ? 'item' : 'itens'}` : 'Nenhum item nesta indústria'}
                        </p>
                      )}

                      {/* Ordem de Compra e Frete da Indústria */}
                      {(pi.ordemCompra || pi.frete) && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Informações de finalização:
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm">
                            {pi.ordemCompra && (
                              <span className="text-gray-900 dark:text-white font-medium">
                                OC: {pi.ordemCompra}
                              </span>
                            )}
                            {pi.frete && (
                              <span className="text-gray-900 dark:text-white font-medium">
                                Frete: {pi.frete}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Observação da Indústria */}
                      {(pi.observacao || pi.observacaoIndustria || pi.observacao_industria) && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Observação:
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                            {pi.observacao || pi.observacaoIndustria || pi.observacao_industria}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  Nenhum item no pedido
                </p>
              )}
            </div>

            {/* Observações Gerais */}
            {selectedPedido.observacao && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Observações Gerais
                </p>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {selectedPedido.observacao}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                    Total de Itens:
                  </span>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {selectedPedido.total || 
                      selectedPedido.industrias?.reduce((total, pi) => 
                        total + (pi.totalItens || pi.itens?.reduce((sum, item) => sum + item.quantidade, 0) || 0), 0
                      ) || 0}
                  </span>
                </div>
              </div>

              {(() => {
                const totalValor = selectedPedido.industrias?.reduce((total, pi) => 
                  total + (pi.itens?.reduce((sum, item) => 
                    sum + (item.quantidade * (item.preco || 0)), 0
                  ) || 0), 0
                ) || 0

                return totalValor > 0 ? (
                  <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-green-900 dark:text-green-100">
                        Valor Total:
                      </span>
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                        R$ {totalValor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ) : null
              })()}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Nenhum pedido selecionado
          </div>
        )}
      </Modal>
    </MainLayout>
  )
}
